import React, { useState, useEffect } from "react";
import { makeStyles, useTheme, StyleRules } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import GridItem from "components/Grid/GridItem";
import GridContainer from "components/Grid/GridContainer";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import Table from "components/Table/Table";
import Button from "components/CustomButtons/Button";
import CustomInput from "components/CustomInput/CustomInput";
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import axios from "axios";
import EmailLogCard from "./EmailLogCard";

const styles = {
  cardCategoryWhite: {
    "&,& a,& a:hover,& a:focus": {
      color: "rgba(255,255,255,.62)",
      margin: "0",
      fontSize: "14px",
      marginTop: "0",
      marginBottom: "0",
    },
    "& a,& a:hover,& a:focus": {
      color: "#FFFFFF",
    },
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "300",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px",
    textDecoration: "none",
    "& small": {
      color: "#777",
      fontSize: "65%",
      fontWeight: "400",
      lineHeight: "1",
    },
  },
  statsContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
    gap: "10px",
    marginBottom: "20px",
  },
  statCard: {
    textAlign: "center",
    padding: "20px",
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    minWidth: "120px",
  },
  statValue: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#333",
  },
  statLabel: {
    fontSize: "12px",
    color: "#666",
    marginTop: "5px",
  },
  filterContainer: {
    display: "flex",
    gap: "15px",
    marginBottom: "20px",
    flexWrap: "wrap",
  },
  statusChip: {
    margin: "0 5px",
  },
  modalContent: {
    padding: "0",
  },
  modalHeader: {
    padding: "24px 24px 16px 24px",
    borderBottom: "1px solid #e0e0e0",
    backgroundColor: "#fafafa",
    "@media (max-width: 960px)": {
      padding: "16px 16px 12px 16px",
    },
  },
  modalSection: {
    padding: "20px 24px",
    borderBottom: "1px solid #f0f0f0",
    "&:last-child": {
      borderBottom: "none",
    },
    "@media (max-width: 960px)": {
      padding: "16px",
    },
  },
  emailInfoGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    "@media (max-width: 600px)": {
      gridTemplateColumns: "1fr",
    },
  },
  infoItem: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  infoLabel: {
    fontSize: "0.75rem",
    fontWeight: "600",
    color: "#666",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  infoValue: {
    fontSize: "0.875rem",
    color: "#333",
    lineHeight: "1.4",
  },
  sectionTitle: {
    fontSize: "1.125rem",
    fontWeight: "600",
    color: "#333",
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  emailPreview: {
    border: "1px solid #ddd",
    padding: "10px",
    borderRadius: "4px",
    maxHeight: "400px",
    overflow: "auto",
    userSelect: "text",
    "& a": {
      pointerEvents: "none !important",
      textDecoration: "none !important",
      color: "inherit !important",
      cursor: "default !important",
    },
    "& button": {
      pointerEvents: "none !important",
      cursor: "default !important",
      opacity: "0.7",
    },
    "& input": {
      pointerEvents: "none !important",
      cursor: "default !important",
    },
    "& form": {
      pointerEvents: "none !important",
    },
    "& select": {
      pointerEvents: "none !important",
      cursor: "default !important",
    },
    "& textarea": {
      pointerEvents: "none !important",
      cursor: "default !important",
    },
  },
  timeline: {
    position: "relative",
    paddingLeft: "30px",
    "&::before": {
      content: '""',
      position: "absolute",
      left: "15px",
      top: "0",
      bottom: "0",
      width: "2px",
      backgroundColor: "#e0e0e0",
    },
  },
  timelineItem: {
    position: "relative",
    marginBottom: "20px",
    "&::before": {
      content: '""',
      position: "absolute",
      left: "-23px",
      top: "8px",
      width: "12px",
      height: "12px",
      borderRadius: "50%",
      backgroundColor: "#fff",
      border: "3px solid #e0e0e0",
      zIndex: 1,
    },
    "&:last-child": {
      marginBottom: "0",
    },
  },
  timelineContent: {
    backgroundColor: "#f8f9fa",
    padding: "12px 16px",
    borderRadius: "8px",
    border: "1px solid #e9ecef",
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      left: "-8px",
      top: "12px",
      width: "0",
      height: "0",
      borderTop: "8px solid transparent",
      borderBottom: "8px solid transparent",
      borderRight: "8px solid #e9ecef",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      left: "-7px",
      top: "13px",
      width: "0",
      height: "0",
      borderTop: "7px solid transparent",
      borderBottom: "7px solid transparent",
      borderRight: "7px solid #f8f9fa",
    },
  },
  timelineHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "8px",
  },
  timelineTime: {
    fontSize: "0.875rem",
    color: "#6c757d",
    fontWeight: "500",
  },
  timelineDetails: {
    fontSize: "0.875rem",
    color: "#495057",
    lineHeight: "1.4",
  },
};

const useStyles = makeStyles(styles as StyleRules);

interface EmailLog {
  _id?: string;
  coachName?: string;
  recipientNames?: unknown;
  sentAt?: string;
  status?: string;
  subject?: string;
  toEmails?: unknown[];
  opens?: unknown[];
  clicks?: unknown[];
  fromName?: string;
  fromEmail?: string;
  deliveryStatus?: { email?: string; status?: string; [key: string]: unknown };
  statusHistory?: Array<{ timestamp?: string; [key: string]: unknown }>;
  [key: string]: unknown;
}

interface EmailStats {
  bounceRate?: number;
  clickRate?: number;
  deliveryRate?: number;
  openRate?: number;
  totalSent?: number;
  [key: string]: unknown;
}

interface EmailPagination {
  currentPage?: number;
  hasNext?: boolean;
  hasPrev?: boolean;
  totalPages?: number;
  [key: string]: unknown;
}

const EmailLogs = (): React.ReactElement => {
  const classes = useStyles() as Record<string, string>;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<EmailStats>({});
  const [pagination, setPagination] = useState<EmailPagination>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    status: "",
    email: "",
    subject: "",
    coach: "",
    recipient: "",
    dateFrom: "",
    dateTo: "",
  });
  const [selectedEmail, setSelectedEmail] = useState<EmailLog | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchEmailLogs = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "20",
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== "")
        ),
      });

      const response = await axios.get<{
        emailLogs: EmailLog[];
        pagination: EmailPagination;
      }>(`/api/email-logs?${params}`, {
        headers: { "auth-token": localStorage.getItem("token") },
      });

      setEmailLogs(response.data.emailLogs);
      setPagination(response.data.pagination);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching email logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters.dateTo) params.append("dateTo", filters.dateTo);

      const response = await axios.get<EmailStats>(
        `/api/email-logs/stats/overview?${params}`,
        {
          headers: { "auth-token": localStorage.getItem("token") },
        }
      );

      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchEmailDetails = async (id: string) => {
    try {
      const response = await axios.get<EmailLog>(`/api/email-logs/${id}`, {
        headers: { "auth-token": localStorage.getItem("token") },
      });
      setSelectedEmail(response.data);
      setModalOpen(true);
    } catch (error) {
      console.error("Error fetching email details:", error);
    }
  };

  useEffect(() => {
    fetchEmailLogs();
    fetchStats();
  }, [filters]);

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const getStatusColor = (status: string) => {
    const colors = {
      sent: "#2196f3",
      delivered: "#4caf50",
      opened: "#ff9800",
      clicked: "#9c27b0",
      bounced: "#f44336",
      rejected: "#f44336",
      spam: "#ff5722",
      unsub: "#607d8b",
    };
    return (colors as Record<string, string>)[status] || "#666";
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const tableData = emailLogs.map((log) => {
    // Combine recipient names with emails for better display
    const recipients = (log.toEmails as unknown[])
      .map((email: unknown, index: number) => {
        const name =
          log.recipientNames && (log.recipientNames as unknown[])[index];
        return name ? `${name} <${email}>` : email;
      })
      .join(", ");

    return [
      formatDate(log.sentAt as string),
      log.subject,
      recipients,
      log.coachName || "N/A",
      <Chip
        label={log.status}
        size="small"
        style={{ backgroundColor: getStatusColor(log.status as string), color: "white" }}
        className={classes.statusChip}
      />,
      <Button
        size="sm"
        color="primary"
        onClick={() => fetchEmailDetails(log._id as string)}
      >
        View
      </Button>,
    ];
  });

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color="primary">
            <h4 className={classes.cardTitleWhite}>Email Statistics</h4>
            <p className={classes.cardCategoryWhite}>
              Overview of email delivery performance
            </p>
          </CardHeader>
          <CardBody>
            <Box className={classes.statsContainer}>
              <Box className={classes.statCard}>
                <div className={classes.statValue}>{stats.totalSent || 0}</div>
                <div className={classes.statLabel}>Total Sent</div>
              </Box>
              <Box className={classes.statCard}>
                <div className={classes.statValue}>
                  {stats.deliveryRate || 0}%
                </div>
                <div className={classes.statLabel}>Delivery Rate</div>
              </Box>
              <Box className={classes.statCard}>
                <div className={classes.statValue}>{stats.openRate || 0}%</div>
                <div className={classes.statLabel}>Open Rate</div>
              </Box>
              <Box className={classes.statCard}>
                <div className={classes.statValue}>{stats.clickRate || 0}%</div>
                <div className={classes.statLabel}>Click Rate</div>
              </Box>
              <Box className={classes.statCard}>
                <div className={classes.statValue}>
                  {stats.bounceRate || 0}%
                </div>
                <div className={classes.statLabel}>Bounce Rate</div>
              </Box>
            </Box>
          </CardBody>
        </Card>
      </GridItem>

      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color="primary">
            <h4 className={classes.cardTitleWhite}>Email Logs</h4>
            <p className={classes.cardCategoryWhite}>
              Complete list of sent emails with delivery status
            </p>
          </CardHeader>
          <CardBody>
            <Box className={classes.filterContainer}>
              <TextField
                label="Email"
                value={filters.email}
                onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                    handleFilterChange("email", e.target.value as string)
                  }
                size="small"
                fullWidth={isMobile}
                style={{ minWidth: isMobile ? "100%" : "200px" }}
              />
              <TextField
                label="Subject"
                value={filters.subject}
                onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                    handleFilterChange("subject", e.target.value as string)
                  }
                size="small"
                fullWidth={isMobile}
                style={{ minWidth: isMobile ? "100%" : "200px" }}
              />
              <TextField
                label="Coach"
                value={filters.coach}
                onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                    handleFilterChange("coach", e.target.value as string)
                  }
                size="small"
                fullWidth={isMobile}
                style={{ minWidth: isMobile ? "100%" : "200px" }}
              />
              <TextField
                label="Recipient"
                value={filters.recipient}
                onChange={(e) =>
                  handleFilterChange("recipient", e.target.value)
                }
                size="small"
                fullWidth={isMobile}
                style={{ minWidth: isMobile ? "100%" : "200px" }}
              />
              <FormControl
                size="small"
                fullWidth={isMobile}
                style={{ minWidth: isMobile ? "100%" : "150px" }}
              >
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                    handleFilterChange("status", e.target.value as string)
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="delivered">Delivered</MenuItem>
                  <MenuItem value="opened">Opened</MenuItem>
                  <MenuItem value="clicked">Clicked</MenuItem>
                  <MenuItem value="bounced">Bounced</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="From Date"
                type="date"
                value={filters.dateFrom}
                onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                    handleFilterChange("dateFrom", e.target.value as string)
                  }
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth={isMobile}
                style={{ minWidth: isMobile ? "100%" : "auto" }}
              />
              <TextField
                label="To Date"
                type="date"
                value={filters.dateTo}
                onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                    handleFilterChange("dateTo", e.target.value as string)
                  }
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Box>

            {isMobile ? (
              <div>
                {emailLogs.map((log) => (
                  <EmailLogCard
                    key={log._id}
                    email={
                      {
                        subject: log.subject,
                        sentAt: log.sentAt,
                        recipientCount: log.toEmails?.length || 0,
                        coach: log.coachName || "N/A",
                        status: log.status,
                      } as React.ComponentProps<typeof EmailLogCard>["email"]
                    }
                    onViewDetails={() => fetchEmailDetails(log._id as string)}
                  />
                ))}
              </div>
            ) : (
              <Table
                tableHeaderColor="primary"
                tableHead={[
                  "Date",
                  "Subject",
                  "Recipients",
                  "Coach",
                  "Status",
                  "Actions",
                ]}
                tableData={tableData}
              />
            )}

            {pagination.totalPages! > 1 && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection={isMobile ? "column" : "row"}
                gap={isMobile ? "8px" : "0"}
                mt={2}
              >
                <Button
                  disabled={!pagination.hasPrev}
                  onClick={() => fetchEmailLogs(currentPage - 1)}
                  size="sm"
                  fullWidth={isMobile}
                >
                  Previous
                </Button>
                <Typography
                  variant="body2"
                  style={{
                    margin: isMobile ? "0" : "0 15px",
                    alignSelf: "center",
                    textAlign: "center",
                  }}
                >
                  Page {pagination.currentPage} of {pagination.totalPages!}
                </Typography>
                <Button
                  disabled={!pagination.hasNext}
                  onClick={() => fetchEmailLogs(currentPage + 1)}
                  size="sm"
                  fullWidth={isMobile}
                >
                  Next
                </Button>
              </Box>
            )}
          </CardBody>
        </Card>
      </GridItem>

      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Email Details
          <IconButton
            aria-label="close"
            onClick={() => setModalOpen(false)}
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers={false} style={{ padding: 0 }}>
          {selectedEmail && (
            <Box className={classes.modalContent}>
              {/* Header Section */}
              <Box className={classes.modalHeader}>
                <Typography
                  variant="h5"
                  style={{
                    fontWeight: 600,
                    color: "#333",
                    marginBottom: "8px",
                  }}
                >
                  {selectedEmail.subject}
                </Typography>
                <Chip
                  label={(selectedEmail.status as string).toUpperCase()}
                  size="medium"
                  style={{
                    backgroundColor: getStatusColor(selectedEmail.status as string),
                    color: "white",
                    fontWeight: "bold",
                  }}
                />
              </Box>

              {/* Email Information Section */}
              <Box className={classes.modalSection}>
                <Typography className={classes.sectionTitle}>
                  Email Information
                </Typography>
                <Box className={classes.emailInfoGrid}>
                  <Box className={classes.infoItem}>
                    <Typography className={classes.infoLabel}>From</Typography>
                    <Typography className={classes.infoValue}>
                      {selectedEmail.fromName} &lt;{selectedEmail.fromEmail}&gt;
                    </Typography>
                  </Box>
                  <Box className={classes.infoItem}>
                    <Typography className={classes.infoLabel}>To</Typography>
                    <Typography className={classes.infoValue}>
                      {(selectedEmail.toEmails as unknown[])
                        .map((email: unknown, index: number) => {
                          const name =
                            selectedEmail.recipientNames &&
                            (selectedEmail.recipientNames as unknown[])[index];
                          return name ? `${name} <${email}>` : email;
                        })
                        .join(", ")}
                    </Typography>
                  </Box>
                  <Box className={classes.infoItem}>
                    <Typography className={classes.infoLabel}>Coach</Typography>
                    <Typography className={classes.infoValue}>
                      {selectedEmail.coachName || "N/A"}
                    </Typography>
                  </Box>
                  <Box className={classes.infoItem}>
                    <Typography className={classes.infoLabel}>
                      Sent Date
                    </Typography>
                    <Typography className={classes.infoValue}>
                      {formatDate(selectedEmail.sentAt as string)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Status Timeline Section */}
              {selectedEmail.statusHistory &&
                selectedEmail.statusHistory.length > 0 && (
                  <Box className={classes.modalSection}>
                    <Typography className={classes.sectionTitle}>
                      Status Timeline
                    </Typography>
                    <Box className={classes.timeline}>
                      {selectedEmail.statusHistory
                        .sort(
                          (
                            a: { timestamp?: string; [key: string]: unknown },
                            b: { timestamp?: string; [key: string]: unknown }
                          ) =>
                            new Date(b.timestamp as string).getTime() -
                            new Date(a.timestamp as string).getTime()
                        )
                        .map((entry: EmailLog, index: number) => (
                          <Box
                            key={index}
                            className={classes.timelineItem}
                            sx={
                              {
                                "&::before": {
                                  borderColor: `${getStatusColor(
                                    entry.status as string
                                  )} !important`,
                                },
                              } as React.ComponentProps<typeof Box>["sx"]
                            }
                          >
                            <Box className={classes.timelineContent}>
                              <Box className={classes.timelineHeader}>
                                <Chip
                                  label={(entry.status as string).toUpperCase()}
                                  size="small"
                                  style={{
                                    backgroundColor: getStatusColor(
                                      entry.status as string
                                    ),
                                    color: "white",
                                    fontWeight: "bold",
                                    fontSize: "0.75rem",
                                  }}
                                />
                                <Typography className={classes.timelineTime}>
                                  {formatDate(entry.timestamp as string)}
                                </Typography>
                              </Box>
                              {entry.details && (
                                <Typography className={classes.timelineDetails}>
                                  {entry.details}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                    </Box>
                  </Box>
                )}

              {/* Delivery Status Section */}
              {selectedEmail.deliveryStatus && (
                <Box className={classes.modalSection}>
                  <Typography className={classes.sectionTitle}>
                    Delivery Status
                  </Typography>
                  <Box className={classes.emailInfoGrid}>
                    <Box className={classes.infoItem}>
                      <Typography className={classes.infoLabel}>
                        Email
                      </Typography>
                      <Typography className={classes.infoValue}>
                        {selectedEmail.deliveryStatus.email}
                      </Typography>
                    </Box>
                    <Box className={classes.infoItem}>
                      <Typography className={classes.infoLabel}>
                        Status
                      </Typography>
                      <Typography className={classes.infoValue}>
                        {selectedEmail.deliveryStatus.status}
                      </Typography>
                    </Box>
                    {selectedEmail.deliveryStatus.reject_reason && (
                      <Box
                        className={classes.infoItem}
                        style={{ gridColumn: "1 / -1" }}
                      >
                        <Typography className={classes.infoLabel}>
                          Reject Reason
                        </Typography>
                        <Typography
                          className={classes.infoValue}
                          style={{ color: "#d32f2f" }}
                        >
                          {selectedEmail.deliveryStatus.reject_reason}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* Opens Section */}
              {selectedEmail.opens && selectedEmail.opens.length > 0 && (
                <Box className={classes.modalSection}>
                  <Typography className={classes.sectionTitle}>
                    Opens ({selectedEmail.opens.length})
                  </Typography>
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    {(selectedEmail.opens as Record<string, unknown>[]).map(
                      (open: Record<string, unknown>, index: number) => (
                      <Box
                        key={index}
                        style={{
                          padding: "12px",
                          backgroundColor: "#f8f9fa",
                          borderRadius: "6px",
                          border: "1px solid #e9ecef",
                        }}
                      >
                        <Typography
                          variant="body2"
                          style={{ fontWeight: 600, marginBottom: "4px" }}
                        >
                          {formatDate(open.timestamp as string)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          IP: {open.ip || "Unknown"} • Location:{" "}
                          {open.location || "Unknown"}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Clicks Section */}
              {selectedEmail.clicks && selectedEmail.clicks.length > 0 && (
                <Box className={classes.modalSection}>
                  <Typography className={classes.sectionTitle}>
                    Clicks ({selectedEmail.clicks.length})
                  </Typography>
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    {(selectedEmail.clicks as Record<string, unknown>[]).map(
                      (click: Record<string, unknown>, index: number) => (
                      <Box
                        key={index}
                        style={{
                          padding: "12px",
                          backgroundColor: "#f8f9fa",
                          borderRadius: "6px",
                          border: "1px solid #e9ecef",
                        }}
                      >
                        <Typography
                          variant="body2"
                          style={{ fontWeight: 600, marginBottom: "4px" }}
                        >
                          {formatDate(click.timestamp as string)}
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{ marginBottom: "4px" }}
                        >
                          <a
                            href={click.url as string}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#1976d2" }}
                          >
                            {click.url}
                          </a>
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          IP: {click.ip || "Unknown"} • Location:{" "}
                          {click.location || "Unknown"}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Email Content Section */}
              {selectedEmail.htmlContent && (
                <Box className={classes.modalSection}>
                  <Typography className={classes.sectionTitle}>
                    Email Content
                  </Typography>
                  <Box
                    dangerouslySetInnerHTML={{
                      __html: selectedEmail.htmlContent,
                    }}
                    className={classes.emailPreview}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </GridContainer>
  );
};

export default EmailLogs;
